import React from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { scrollToSection } from './LandingHeader';

export function HeroSection({ copy }) {
  const hero = copy.hero;
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[#0A0A0A] pb-24 pt-36 sm:pt-44 lg:pb-32"
      aria-labelledby="hero-title"
      data-testid="hero-section"
    >
      {/* Ambient gold light + fine grid, both decorative */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 70% at 78% 40%, rgba(212,175,55,0.20) 0%, rgba(212,175,55,0.05) 45%, transparent 70%), radial-gradient(40% 50% at 8% 100%, rgba(85,107,47,0.18) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(248,246,240,1) 1px, transparent 1px), linear-gradient(90deg, rgba(248,246,240,1) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(70% 70% at 70% 45%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(70% 70% at 70% 45%, black, transparent)',
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <p className="font-landing text-xs font-semibold uppercase tracking-[0.32em] text-[#D4AF37]" data-reveal>
            <span className="mr-3 inline-block h-px w-10 bg-[#D4AF37] align-middle" />
            {hero.eyebrow}
          </p>
          <h1
            id="hero-title"
            className="mt-6 font-display text-4xl font-semibold leading-[1.08] text-[#F8F6F0] sm:text-5xl lg:text-[4rem]"
            data-reveal
            style={{ '--reveal-delay': '80ms' }}
          >
            {hero.titleStart}{' '}
            <span className="italic text-[#D4AF37]">{hero.titleAccent}</span>
          </h1>
          <p
            className="mt-7 max-w-xl font-landing text-base leading-relaxed text-[#CFC8BE] sm:text-lg"
            data-reveal
            style={{ '--reveal-delay': '160ms' }}
          >
            {hero.description}
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row" data-reveal style={{ '--reveal-delay': '240ms' }}>
            <button
              type="button"
              onClick={() => scrollToSection('products')}
              className="group inline-flex h-14 items-center justify-center gap-3 bg-gradient-to-b from-[#E2C15A] to-[#C39B22] px-8 font-landing text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#0A0A0A] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F8F6F0]"
              data-testid="hero-cta-btn"
            >
              {hero.primary}
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('contact')}
              className="inline-flex h-14 items-center justify-center border border-[#F8F6F0]/50 px-8 font-landing text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#F8F6F0] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
            >
              {hero.secondary}
            </button>
          </div>

          <ul className="mt-12 flex flex-wrap gap-x-8 gap-y-3" data-reveal style={{ '--reveal-delay': '320ms' }}>
            {hero.chips.map((chip) => (
              <li key={chip} className="flex items-center gap-3 font-landing text-xs font-medium uppercase tracking-[0.2em] text-[#CFC8BE]">
                <span className="h-1.5 w-1.5 rotate-45 bg-[#D4AF37]" aria-hidden="true" />
                {chip}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none" aria-hidden="true">
          <div className="absolute inset-0 m-auto h-[85%] w-[85%] rounded-full border border-[#D4AF37]/25" />
          <div className="absolute inset-0 m-auto h-[68%] w-[68%] rounded-full border border-[#D4AF37]/15" />
          <img
            src="/brand/symbol.webp"
            alt=""
            className="landing-float relative mx-auto w-[78%] drop-shadow-[0_20px_60px_rgba(212,175,55,0.28)]"
            width="520"
            height="498"
          />
        </div>
      </div>

      <div className="relative mx-auto mt-20 max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 justify-items-center gap-x-6 gap-y-4 border-t border-[#D4AF37]/25 pt-8 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-10">
          {hero.values.map((value, index) => (
            <span key={value} className="flex items-center gap-10 text-center font-display text-xs uppercase tracking-[0.24em] text-[#CFC8BE] sm:text-sm sm:tracking-[0.3em]">
              {value}
              {index < hero.values.length - 1 && <span className="hidden h-1.5 w-1.5 rotate-45 bg-[#D4AF37] sm:block" aria-hidden="true" />}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
